import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { from, throwError } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { IPaginatedResponse } from "../models/api/paginated-response";
import { IPaginationSearchParams } from "../models/api/pagination-search-params";
import { ICreateFaxFolderSearchParams } from "../models/fax/create-fax-folder-search-params";
import { ICreateFaxRulePayload } from "../models/fax/create-fax-rule-payload";
import { IDeleteFaxRulePayload } from "../models/fax/delete-fax-rule-payload";
import { IFax } from "../models/fax/fax";
import { IFaxFolder } from "../models/fax/fax-folder";
import { IFaxFolderSearchParams } from "../models/fax/fax-folder-search-params";
import { IFaxRule } from "../models/fax/fax-rule";
import { IFaxesSearchParams } from "../models/fax/faxes-search-params";
import { ICopyFaxPayload } from "../models/fax/copy-fax-payload";
import { IForwardFaxPayload } from "../models/fax/forward-fax-payload";
import { IGenerateFaxPdfPayload } from "../models/fax/generate-fax-pdf-payload";

@Injectable({ providedIn: "root" })
export class FaxService {
  constructor(private readonly _httpClient: HttpClient) {}

  getAll(params: IFaxesSearchParams) {
    return this._httpClient.get<IPaginatedResponse<IFax>>(this.withBaseUrl("/all"), {
      params: this.omitEmptyParams(params),
    });
  }

  getOne(faxId: string) {
    return this._httpClient.get<IFax>(this.withBaseUrl(`/${faxId}`));
  }

  delete(faxId: string) {
    return this._httpClient.delete<void>(this.withBaseUrl(`/${faxId}`));
  }

  saveToFolder(faxId: string, folderName: string) {
    return this._httpClient.post<void>(this.withBaseUrl(`/to-folder/${faxId}`), null, {
      params: { folderName },
    });
  }

  forward(faxId: number, payload: IForwardFaxPayload) {
    return this._httpClient.post<void>(this.withBaseUrl(`/forward/${faxId}`), payload);
  }

  copy(faxId: number, payload: ICopyFaxPayload) {
    return this._httpClient.post<void>(this.withBaseUrl(`/copy/${faxId}`), payload);
  }

  generatePdf(payload: IGenerateFaxPdfPayload) {
    const formData = new FormData();

    formData.append("toNumber", payload.toNumber);
    formData.append("fromNumber", payload.fromNumber);
    formData.append("deliveryTo", payload.deliveryTo ?? "");
    formData.append("sentFrom", payload.sentFrom ?? "");
    formData.append("generateCoverPage", payload.generateCoverPage ? "true" : "false");
    formData.append("faxSubject", payload.faxSubject ?? "");
    formData.append("notes", payload.notes ?? "");

    for (let file of payload.files) {
      formData.append("files", file, file.name);
    }

    const pdfPromise = fetch(this.withBaseUrl("/files"), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
      },
      body: formData,
    }).then(response => {
      if (!response.ok) {
        throw new Error(response.status === 400
          ? "Failed to generate fax PDF. Attach file and try again"
          : `Failed to generate fax PDF: ${response.status}`);
      }

      return response.blob();
    });

    return from(pdfPromise);

    // return this._httpClient.post<Blob>(this.withBaseUrl("/files"), formData, {
    //   headers: {
    //     Accept: "application/pdf",
    //   },
    // });
  }

  getFileSrc(faxId: string) {
    return this._httpClient.get(this.withBaseUrl(`/file/${faxId}`), { responseType: "blob" }).pipe(
      map(blob => URL.createObjectURL(blob)),
      catchError(error => this.parseBlobError(error))
    );
  }

  private parseBlobError(error: HttpErrorResponse) {
    if (error.error instanceof Blob) {
      return from(error.error.text()).pipe(
        switchMap(text => throwError(new Error(this.extractErrorMessage(text) || error.message)))
      );
    }

    return throwError(error);
  }

  private extractErrorMessage(text: string) {
    try {
      return JSON.parse(text)?.message ?? text;
    } catch {
      return text;
    }
  }

  createFolder(params: ICreateFaxFolderSearchParams) {
    return this._httpClient.post<void>(this.withBaseUrl("/folder"), {}, { params });
  }

  getFolders(params: IPaginationSearchParams) {
    return this._httpClient.get<IPaginatedResponse<IFaxFolder>>(this.withBaseUrl("/folders"), { params });
  }

  getFolder(params: IFaxFolderSearchParams) {
    return this._httpClient.get<IFaxFolder>(this.withBaseUrl("/folder"), { params });
  }

  deleteFolder(params: IFaxFolderSearchParams) {
    return this._httpClient.delete<void>(this.withBaseUrl("/folder"), { params });
  }

  createRule(payload: ICreateFaxRulePayload) {
    return this._httpClient.post<void>(this.withBaseUrl("/rule"), payload);
  }

  getRules(params: IPaginationSearchParams) {
    return this._httpClient.get<IPaginatedResponse<IFaxRule>>(this.withBaseUrl("/rules"), { params });
  }

  deleteRule(payload: IDeleteFaxRulePayload) {
    return this._httpClient.delete<void>(this.withBaseUrl("/rule"), { body: payload });
  }

  private withBaseUrl(endpoint: string) {
    return `${environment.serverUrl}/fax${endpoint}`;
  }

  private omitEmptyParams<T extends Record<string, unknown>>(params: T): { [key: string]: string | number | boolean } {
    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== undefined && value !== null)
    ) as { [key: string]: string | number | boolean };
  }
}
